import { IGenericModal, Modal } from 'components/Modal/Modal';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildPlatformPracticePayload,
  PlatformPracticeForm
} from '../PlatformPracticeForm/PlatformPracticeForm';
import { IPlatformPractice, IPlatformPracticeFormFields } from 'types/platformPractice';
import { createPlatformPractice } from 'api/platformPractices';
import { showPlatformApiError } from 'views/PlatformView/platformApiError';
import { yupResolver } from '@hookform/resolvers/yup';
import { platformPracticeSchema } from 'validation/platformPracticeSchemas';

interface Props extends IGenericModal {
  onSuccess?: (practice: IPlatformPractice) => void;
  defaultMethodology?: { id: number; name: string };
}

export const AddPlatformPracticeForm = ({
  isOpen,
  onCancelClick,
  onSuccess,
  defaultMethodology
}: Props) => {
  const { t } = useTranslation();

  const methods = useForm<IPlatformPracticeFormFields>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      id_code: '',
      color_code: '#96c31e',
      state: 'active',
      methodology_id: defaultMethodology ? String(defaultMethodology.id) : ''
    },
    resolver: yupResolver(platformPracticeSchema)
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (!isOpen) return;

    reset({
      name: '',
      id_code: '',
      color_code: '#96c31e',
      state: 'active',
      methodology_id: defaultMethodology ? String(defaultMethodology.id) : ''
    });
  }, [isOpen, defaultMethodology, reset]);

  const handleSubmitForm = async (data: IPlatformPracticeFormFields) => {
    try {
      const response = await createPlatformPractice(buildPlatformPracticePayload(data));
      onSuccess?.(response.data);
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      headerTitle={t('platformPractices.addPractice')}
      mainButton={{
        action: handleSubmit(handleSubmitForm),
        variant: 'eucalyptus',
        label: t('buttons.save')
      }}
      isSecondButtonVisible
      gridTemplateColumns="70%"
    >
      <FormProvider {...methods}>
        <form>
          <PlatformPracticeForm
            lockedMethodology={
              defaultMethodology
                ? {
                    id: String(defaultMethodology.id),
                    name: defaultMethodology.name
                  }
                : undefined
            }
          />
        </form>
      </FormProvider>
    </Modal>
  );
};
